import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithErrorHandling } from './baseQuery';

// Types
export interface GenerateDocxRequest {
  matter_id: string;
  template_type: 'demand' | 'lor' | 'hipaa';
}

export interface EditorConfig {
  document_server_url: string;
  config: {
    document: {
      fileType: string;
      key: string;
      title: string;
      url: string;
    };
    editorConfig: {
      callbackUrl: string;
      user: { id: string; name: string };
      customization: Record<string, unknown>;
      mode: string;
    };
    documentType: string;
    token?: string;
  };
}

export interface GenerateDocxResponse {
  blob_id: string;
  filename: string;
  download_url: string;
  editor_config: EditorConfig;
}

export interface EditDocumentRequest {
  blob_id: string;
  instructions: string;
}

export interface EditDocumentResponse {
  success: boolean;
  message: string;
  blob_id: string;
}

export interface BlobToHtmlResponse {
  blob_id: string;
  html: string;
  paragraph_count: number;
}

export interface HtmlToDocxRequest {
  blob_id: string;
  html: string;
}

export interface HtmlToDocxResponse {
  blob_id: string;
  success: boolean;
  paragraphs_changed: number;
}

export interface SectionInfo {
  tag: string;
  alias: string;
  is_repeatable: boolean;
  child_count: number;
  paragraph_count: number;
}

export interface ListSectionsResponse {
  blob_id: string;
  sections: SectionInfo[];
}

export interface SectionContentResponse {
  blob_id: string;
  section_tag: string;
  html: string;
  paragraph_count: number;
}

export interface ApplySectionHtmlRequest {
  blob_id: string;
  section_tag: string;
  html: string;
}

export interface ApplySectionHtmlResponse {
  blob_id: string;
  success: boolean;
  paragraphs_changed: number;
}

export const onlyofficeDemoApi = createApi({
  reducerPath: 'onlyofficeDemoApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['OnlyOfficeDemo'] as const,
  endpoints: (builder) => ({
    generateDocx: builder.mutation<GenerateDocxResponse, GenerateDocxRequest>({
      query: (body) => ({
        url: 'api/internal/onlyoffice-demo/generate/',
        method: 'POST',
        body,
      }),
    }),
    uploadAndEdit: builder.mutation<GenerateDocxResponse, FormData>({
      query: (formData) => ({
        url: 'api/internal/onlyoffice-demo/upload/',
        method: 'POST',
        body: formData,
      }),
    }),
    getEditorConfig: builder.query<EditorConfig, { blobId: string; _t?: number }>({
      query: ({ blobId, _t }) =>
        `api/internal/onlyoffice-demo/config/${blobId}/${_t ? `?_t=${_t}` : ''}`,
    }),
    editDocument: builder.mutation<EditDocumentResponse, EditDocumentRequest>({
      query: (body) => ({
        url: 'api/internal/onlyoffice-demo/edit/',
        method: 'POST',
        body,
      }),
    }),
    getBlobAsHtml: builder.query<
      BlobToHtmlResponse,
      { blobId: string; _t?: number }
    >({
      query: ({ blobId, _t }) =>
        `api/internal/onlyoffice-demo/blob-to-html/${blobId}/${_t ? `?_t=${_t}` : ''}`,
    }),
    applyHtmlToDocx: builder.mutation<HtmlToDocxResponse, HtmlToDocxRequest>({
      query: (body) => ({
        url: 'api/internal/onlyoffice-demo/html-to-docx/',
        method: 'POST',
        body,
      }),
    }),
    listSections: builder.query<ListSectionsResponse, { blobId: string }>({
      query: ({ blobId }) => `/api/internal/onlyoffice-demo/sections/${blobId}/`,
    }),
    getSectionHtml: builder.query<
      SectionContentResponse,
      { blobId: string; sectionTag: string }
    >({
      query: ({ blobId, sectionTag }) =>
        `/api/internal/onlyoffice-demo/sections/${blobId}/${encodeURIComponent(sectionTag)}/html/`,
    }),
    applySectionHtml: builder.mutation<ApplySectionHtmlResponse, ApplySectionHtmlRequest>({
      query: (body) => ({
        url: '/api/internal/onlyoffice-demo/sections/apply-html/',
        method: 'POST',
        body,
      }),
    }),
    saveBlob: builder.mutation<
      { blob_id: string; success: boolean },
      { blobId: string; file: Blob }
    >({
      query: ({ blobId, file }) => {
        const formData = new FormData();
        formData.append('file', file, 'document.docx');
        return {
          url: `api/internal/onlyoffice-demo/save-blob/${blobId}/`,
          method: 'PUT',
          body: formData,
        };
      },
    }),
    saveDocumentFile: builder.mutation<
      { document_id: string; success: boolean },
      { documentId: string; file: Blob }
    >({
      query: ({ documentId, file }) => {
        const formData = new FormData();
        formData.append('file', file, 'document.docx');
        return {
          url: `api/internal/onlyoffice-demo/save-document/${documentId}/`,
          method: 'PUT',
          body: formData,
        };
      },
    }),
  }),
});

export const {
  useGenerateDocxMutation,
  useUploadAndEditMutation,
  useGetEditorConfigQuery,
  useLazyGetEditorConfigQuery,
  useEditDocumentMutation,
  useGetBlobAsHtmlQuery,
  useLazyGetBlobAsHtmlQuery,
  useApplyHtmlToDocxMutation,
  useLazyListSectionsQuery,
  useLazyGetSectionHtmlQuery,
  useApplySectionHtmlMutation,
  useSaveBlobMutation,
  useSaveDocumentFileMutation,
} = onlyofficeDemoApi;
